import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import SaveIcon from "@mui/icons-material/Save";
import { Alert, Button, Checkbox, Snackbar, TextField } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { formatISO } from "date-fns";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CategoryPicker } from "../../components/CategoryPicker";
import { DevicePicker } from "../../components/DevicePicker";
import { useFaqGet } from "../../services/Dynamo/get";
import { useFaqMutation } from "../../services/Dynamo/mutation";
import { CountryKey } from "../../utils/models/Common.model";
import { Faq } from "../../utils/models/Faq.model";
import { TabsLangFaq } from "./pieces/TabsLangFaq";

export default function NewFAQ() {
  const navigate = useNavigate();
  const { uidFAQ } = useParams();
  const isModify = uidFAQ !== "new";
  const {
    isLoading,
    item: faq,
    refetch,
    isStale
  } = useFaqGet(uidFAQ as string, isModify);
  const {
    isLoading: isLoadinMut,
    error: errorMut,
    mutateItem,
  } = useFaqMutation();

  const [langs, setLangs] = useState<CountryKey[]>([]);

  // campi
  const [name, setName] = useState("");
  const [order, setOrder] = useState(0);
  const [checked, setChecked] = useState(false);
  const [devicesIds, setDevicesUid] = useState<Faq["devices_ids"]>([]);
  const [categoryIds, setCategoryUid] = useState<Faq["categories"]>([]);
  const [translations, setTranslations] = useState<Faq["translations"]>({});
  const [showNotificationSuccess, setShowNotificationSuccess] = useState<boolean>(false)

  const handleChangeString =
    (setter: Dispatch<SetStateAction<string>>) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setter(event.target.value);
      };
  const handleChangeNumber =
    (setter: Dispatch<SetStateAction<number>>) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setter(parseInt(event.target.value));
      };

  useEffect(() => {
    setName(faq?.name || "");
    setOrder(faq?.order || 0);
    setChecked(faq?.published || false);
    setDevicesUid(faq?.devices_ids || []);
    setCategoryUid(faq?.categories || []);
    setTranslations(faq?.translations || {});
  }, [faq]);

  const handleSave = () => {
    let oldFaq = faq || {};
    oldFaq = {
      ...oldFaq,
      name,
      order,
      published: checked,
      devices_ids: devicesIds,
      translations,
      categories: categoryIds,
    } as Faq
    if (isModify) {
      oldFaq = {
        ...oldFaq,
        updated_at: formatISO(new Date()),
      } as Faq
    } else {
      oldFaq = {
        ...oldFaq,
        created_at: formatISO(new Date()),
      }
    }
    mutateItem(oldFaq as Faq).then((res) => {
      setShowNotificationSuccess(true)
      setTimeout(() => {
        setShowNotificationSuccess(false)
        if (isModify) {
          refetch();
        } else {
          navigate(`/faq/${res.uid}`);
        }
      }, 2000)
    });
  };

  if (!faq && !isStale && isLoading === false && isModify) {
    navigate("../");
  }
  const isFormValid = name !== "" && order > 0 && devicesIds.length > 0 && categoryIds.length > 0
  return (
    <div className="container mx-auto">
      {isLoading && (
        <div className="page-loading">
          <CircularProgress />
        </div>
      )}
      {(!isLoading || !isModify) && (
        <>
          <Link to=".." relative="path">
            <Button
              className="buttons-style"
              variant="contained"
              onClick={() => {
                navigate("../");
              }}
            >
              <ArrowCircleLeftIcon className="icon-button" />{" "}
              <span className="w-full">Go back</span>
            </Button>
          </Link>
          <div className="my-4">
            <hr></hr>
          </div>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl">
              {isModify ? `Edit FAQ: ${faq?.name}` : "New FAQ"}
            </h1>
            <Button
              disabled={!isFormValid}
              variant="contained"
              onClick={handleSave}
              className="buttons-style"
            >
              <SaveIcon className="icon-button" />
              <span className="w-full">Save</span>
            </Button>
          </div>

          <div>
            <div className="grid grid-rows-1 grid-cols-6 gap-2">
              <div className="col-span-4">
                <p className="font-semibold">Name</p>
                <div className="mt-1">
                  <TextField
                    error={name === ''}
                    fullWidth
                    label="Name"
                    variant="outlined"
                    size="small"
                    type="text"
                    name="name"
                    value={name}
                    onChange={handleChangeString(setName)}
                  />
                </div>
              </div>
              <div className="col-span-1">
                <p className="font-semibold">Order</p>
                <div className="mt-1">
                  <TextField
                    error={order === 0}
                    fullWidth
                    label="Order"
                    variant="outlined"
                    size="small"
                    type="number"
                    name="order"
                    value={order}
                    onChange={handleChangeNumber(setOrder)}
                  />
                </div>
              </div>
              <div className="col-span-1">
                <p className="font-semibold">Public</p>
                <div className="mt-1">

                  <Checkbox
                    checked={checked}
                    onChange={(event) => setChecked(event.target.checked)}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-rows-1 grid-cols-2 gap-2">
              <div>
                <DevicePicker
                  selected={devicesIds}
                  onChange={setDevicesUid}
                  onChangeLangs={setLangs}
                />
              </div>
              <div>
                <CategoryPicker
                  selected={categoryIds}
                  onChange={setCategoryUid}
                />
              </div>
            </div>
          </div>
          <div>
            <TabsLangFaq
              langs={langs}
              translations={translations}
              setTranslations={setTranslations}
            />
          </div>
        </>
      )}
      {showNotificationSuccess &&
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={true}
          message="Operation carried out successfully"
        >
          <Alert sx={{ color: "white", backgroundColor: "#1D4EBB" }} severity="info">
            Operation carried out successfully
          </Alert>

        </Snackbar>
      }
    </div>
  );
}
